import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Spinner, Card } from 'flowbite-react';
import { useDatabase } from '../../hooks/useDatabase';
import { useResourceGroup } from '../../hooks/useResourceGroup';
import EditableTitle from '../../components/EditableTitle';
import EntitySearchAndCreate from '../../components/common/EntitySearchAndCreate';
import NativeResourceForm from '../../components/forms/NativeResourceForm';
import ResourceReservationPanel from '../../components/planning/ResourceReservationPanel';
import { useWorkspace } from '../../context/WorkspaceContext';

const ResourceGroupDetailPage: React.FC = () => {
  const { groupId, workspaceId } = useParams<{ groupId: string; workspaceId: string }>();
  const { group, loading, error } = useResourceGroup(groupId);
  const { applyCommand, database, safeData } = useDatabase();
  const { buildUrl } = useWorkspace();

  const [directResourceIds, setDirectResourceIds] = useState<string[]>([]);
  const [subgroupIds, setSubgroupIds] = useState<string[]>([]);
  const [allResourceIds, setAllResourceIds] = useState<string[]>([]);
  const [createForm, setCreateForm] = useState<{ open: boolean; defaultName: string }>({
    open: false,
    defaultName: '',
  });

  // Default 6-week window starting Monday of the current week — matches the
  // resource detail page so the panel feels consistent across contexts.
  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    const dow = now.getDay();
    const back = (dow + 6) % 7;
    const start = new Date(now);
    start.setDate(start.getDate() - back);
    const end = new Date(start);
    end.setDate(start.getDate() + 41);
    return {
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
    };
  }, []);

  const teams = useMemo(() => {
    if (!database) return [];
    return Object.entries(database.teams()).map(([id, t]: [string, any]) => ({ id, name: t.name() }));
  }, [database]);

  const resources = useMemo(() => {
    if (!database) return [];
    return Object.entries(database.resources()).map(([id, r]: [string, any]) => ({ id, name: r.name() }));
  }, [database]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!database || !groupId || !safeData) return;
      const groupObj = database.resource_groups()[groupId];
      if (!groupObj) return;
      const [r, g, all] = await Promise.all([
        groupObj.member_resource_ids(safeData),
        groupObj.member_group_ids(safeData),
        groupObj.all_resource_ids(safeData),
      ]);
      if (!cancelled) {
        setDirectResourceIds(r as string[]);
        setSubgroupIds(g as string[]);
        setAllResourceIds(all as string[]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [database, groupId, safeData]);

  const updateName = async (name: string) => {
    if (!groupId) return;
    await applyCommand({
      type: 'UpdateResourceGroup',
      id: groupId,
      actions: [{ type: 'SetName', name }],
    });
  };

  const updateTeam = async (teamId: string) => {
    if (!groupId) return;
    await applyCommand({
      type: 'UpdateResourceGroup',
      id: groupId,
      actions: [{ type: 'SetManagedByTeam', team_id: teamId || null }],
    });
  };

  const linkResourceToGroup = async (resourceId: string) => {
    if (!groupId) return;
    await applyCommand({
      type: 'SetResourceGroupMembership',
      membership: {
        member: { type: 'Resource', value: resourceId },
        group: groupId,
      },
      enabled: true,
    });
  };

  const removeMember = async (resourceId: string) => {
    if (!groupId) return;
    await applyCommand({
      type: 'SetResourceGroupMembership',
      membership: {
        member: { type: 'Resource', value: resourceId },
        group: groupId,
      },
      enabled: false,
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="text-center py-12 text-cm-text-muted">
        {error || 'Resource group not found'}
      </div>
    );
  }

  const resourceNameById: Record<string, string> = {};
  for (const r of resources) resourceNameById[r.id] = r.name;

  // Resources eligible to add: anything not already a member of this group.
  const candidateResources = resources.filter((r) => !directResourceIds.includes(r.id));

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <EditableTitle title={group.name} onTitleChange={updateName} />

      <Card>
        <h3 className="font-mono font-medium text-cm-text-primary mb-3">Details</h3>
        <div>
          <label className="text-sm text-cm-text-muted block mb-1">Managed by team</label>
          <select
            className="w-full bg-cm-bg-elevated border border-cm-border-subtle rounded-lg p-2 text-cm-text-primary text-sm"
            value={group.managedByTeamId || ''}
            onChange={(e) => updateTeam(e.target.value)}
          >
            <option value="">— none —</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-mono font-medium text-cm-text-primary">Members</h3>
        </div>

        <div className="mb-4">
          <EntitySearchAndCreate
            candidates={candidateResources}
            onPickExisting={linkResourceToGroup}
            onCreateNew={(name) => setCreateForm({ open: true, defaultName: name })}
            createLabel="Create new resource"
            placeholder="Find or create a resource…"
            compact
          />
        </div>

        {directResourceIds.length === 0 && subgroupIds.length === 0 ? (
          <p className="text-cm-text-muted text-sm font-mono py-4 text-center">
            No members yet.
          </p>
        ) : (
          <ul className="divide-y divide-cm-border-subtle">
            {directResourceIds.map((id) => (
              <li key={id} className="py-2 flex items-center justify-between">
                <Link
                  to={buildUrl({ kind: 'resource', resourceId: id })}
                  className="text-cm-accent-blue hover:underline"
                >
                  {resourceNameById[id] ?? id}
                </Link>
                <button
                  onClick={() => removeMember(id)}
                  className="text-xs text-cm-accent-red hover:underline"
                >
                  remove
                </button>
              </li>
            ))}
            {subgroupIds.map((id) => (
              <li key={id} className="py-2 flex items-center justify-between">
                <Link
                  to={buildUrl({ kind: 'resource-group', groupId: id })}
                  className="text-cm-accent-teal hover:underline"
                >
                  [group] {id}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <h3 className="font-mono font-medium text-cm-text-primary mb-3">Reservations</h3>
        <ResourceReservationPanel
          resourceIds={allResourceIds}
          startDate={startDate}
          endDate={endDate}
          intervalDays={7}
          workspaceId={workspaceId}
          emptyMessage="No member resources, or none with reservations in the current window."
        />
      </Card>

      <NativeResourceForm
        isOpen={createForm.open}
        defaultName={createForm.defaultName}
        // The new resource will inherit the group's manager team if any —
        // a reasonable default since groups and their resources are typically
        // managed by the same team. The user can change it before submitting.
        defaultManagedByTeamId={group.managedByTeamId}
        onClose={() => setCreateForm({ open: false, defaultName: '' })}
        onSubmit={applyCommand}
        onCreated={(newResourceId) => {
          // Auto-link the new resource into this group.
          linkResourceToGroup(newResourceId);
        }}
      />
    </div>
  );
};

export default ResourceGroupDetailPage;
